/**
 * Analytics Service
 * Tracks pageviews and click events
 */

export const trackArtistProductClick = (productId, productName, artistId, artistSlug) => {
  trackClick(`artist-product-${productId}`, 'link', productName, {
    productId,
    productName,
    artistId: artistId || null,
    artistSlug: artistSlug || null,
  });
};

export const trackArtistContact = (artistId, artistSlug, method) => {
  trackClick(`artist-contact-${artistId || artistSlug}`, 'form', 'Contact Form', {
    artistId: artistId || null,
    artistSlug: artistSlug || null,
    contactMethod: method || null,
  });
};

export const trackArtistGuideTermsClick = () => {
  trackClick('artist-guide-terms-link', 'link', 'Términos completos');
};

// ---------------------------------------------------------------------------
// Taipei Toy Festival funnel (/pedidos/taipei-toy-festival)
// ---------------------------------------------------------------------------
// Steps are stored as eventType 'ttf' with the step in elementId and a
// per-tab session id in additionalData, so /admin/ttf-metrics can count how
// many visits reach each step (Events has no session column of its own).
const TTF_SESSION_KEY = 'bluppimart.ttfSession';
let ttfSessionFallback = null;

const getTtfSessionId = () => {
  try {
    let id = window.sessionStorage.getItem(TTF_SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(TTF_SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode / blocked storage: one id per page load.
    if (!ttfSessionFallback) ttfSessionFallback = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return ttfSessionFallback;
  }
};

export const TTF_STEPS = {
  view: 'view',
  addPiece: 'add_piece',
  removePiece: 'remove_piece',
  calculatorUsed: 'calculator_used',
  checkoutSubmit: 'checkout_submit',
  orderCreated: 'order_created',
  orderFailed: 'order_failed',
  requestSubmitted: 'request_submitted',
};

export const trackTtfStep = (step, data = {}) => {
  sendEvent({
    eventType: 'ttf',
    path: window.location.pathname,
    elementId: step,
    elementType: 'ttf',
    sessionId: getTtfSessionId(),
    ...data,
  }).catch(() => {}); // fire and forget
};

// Track a pageview
export const trackPageView = (pathname, additionalData = {}) => {
  const eventData = {
    eventType: 'pageview',
    path: pathname,
    ...additionalData
  };

  // Send to API (fire and forget - don't block UI)
  sendEvent(eventData).catch(error => {
    console.error('Error tracking pageview:', error);
  });
};

// Track a click event
export const trackClick = (elementId, elementType, elementText, additionalData = {}) => {
  const eventData = {
    eventType: 'click',
    path: window.location.pathname,
    elementId: elementId || null,
    elementType: elementType || 'unknown',
    elementText: elementText || null,
    ...additionalData
  };

  // Send to API (fire and forget - don't block UI)
  sendEvent(eventData).catch(error => {
    console.error('Error tracking click:', error);
  });
};

// Send event to API
const sendEvent = async (eventData) => {
  if (process.env.NODE_ENV !== 'production') return;
  try {
    const response = await fetch('/api/track-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...eventData,
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenWidth: window.screen?.width || null,
        screenHeight: window.screen?.height || null,
        viewportWidth: window.innerWidth || null,
        viewportHeight: window.innerHeight || null,
        referrer: document.referrer || null,
        url: window.location.href
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    // Silently fail - don't interrupt user experience
    console.error('Failed to send event:', error);
    throw error;
  }
};

